import { z } from 'zod';

/**
 * The error shape the API returns and the client renders.
 *
 * Boilerplate plan §3.D.5: deciding this once is cheaper than harmonising forty
 * route handlers later. These are transport-level outcomes only — no
 * feature-specific codes belong here. A feature that needs to say something
 * more specific says it in `message` and `details`.
 */

export const ERROR_CODES = [
  'bad_request',
  'validation_failed',
  'unauthenticated',
  'forbidden',
  'not_found',
  'conflict',
  'payload_too_large',
  'rate_limited',
  'internal',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

const STATUS_BY_CODE: Record<ErrorCode, number> = {
  bad_request: 400,
  validation_failed: 422,
  unauthenticated: 401,
  forbidden: 403,
  not_found: 404,
  conflict: 409,
  payload_too_large: 413,
  rate_limited: 429,
  internal: 500,
};

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.enum(ERROR_CODES),
    /** Human-readable, safe to show a user. Never contains internal detail. */
    message: z.string(),
    /** Field-level detail, keyed by path. Populated for `validation_failed`. */
    details: z.record(z.string(), z.array(z.string())).optional(),
    /** Correlates a user-visible failure with a server log line. */
    requestId: z.string().optional(),
  }),
});

export type ApiErrorBody = z.infer<typeof apiErrorSchema>;

export function httpStatusForErrorCode(code: ErrorCode): number {
  return STATUS_BY_CODE[code];
}

/**
 * Throw this from anywhere in a route; the server's error handler turns it into
 * the response body above. Anything else that escapes a handler becomes a 500
 * with a generic message, because an unplanned error message is not vetted for
 * disclosure.
 */
export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: Record<string, string[]> | undefined;

  constructor(code: ErrorCode, message: string, details?: Record<string, string[]>) {
    super(message);
    this.code = code;
    this.status = httpStatusForErrorCode(code);
    this.details = details;
  }

  toBody(requestId?: string): ApiErrorBody {
    return {
      error: {
        code: this.code,
        message: this.message,
        ...(this.details ? { details: this.details } : {}),
        ...(requestId ? { requestId } : {}),
      },
    };
  }
}

export const notFound = (what = 'Not found'): ApiError => new ApiError('not_found', what);
export const forbidden = (why = 'Not permitted'): ApiError =>
  new ApiError('forbidden', why);
export const unauthenticated = (why = 'Sign in to continue'): ApiError =>
  new ApiError('unauthenticated', why);
